<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { goto } from '$app/navigation';
  import { Search, Loader2, Building2, Layers, X } from 'lucide-svelte';

  import {
    suggest,
    SearchRefused,
    type SegmentSuggestion,
    type CompanySuggestion
  } from '$lib/api/fullscope';
  import { submitFilters } from '$lib/api/filters';
  import { selectedIndustryCentroids } from '$lib/stores/data';
  import { showResults } from '$lib/stores/navigate';
  import { notifications } from '$lib/stores/notifications';

  /**
   * "Take me to X."
   *
   * # Why this is in the top bar and not a screen
   *
   * Because it is navigation, not a mode. Looking up a company you can already
   * name — by its name, its web address or its register identifier — is
   * something a customer wants from wherever they happen to be: reading a
   * profile, halfway through the guide, looking at a saved list. A screen you
   * have to travel to in order to travel somewhere is a mode, and modes are
   * what made this app feel like a pile of them.
   *
   * # It is deliberately not the offering search
   *
   * This box answers "which firm is this". The other question — "who does this
   * kind of work" — is answered by the offering search in the builder, because
   * it produces a *set* that wants filtering and buying rather than one row.
   *
   * Mixing them is what the single box did, and it did both jobs at once: a
   * company whose marketing copy mentioned the word crowded out the company
   * actually named for it. See `domain.SuggestionScopes` and
   * `domain.OfferingScopes`.
   *
   * # Segments ride along
   *
   * Naming an industry is the same kind of act — "take me to
   * Elektroinstallation" — even though what you land on is a selection rather
   * than one company. So a segment here is a jump that hands off into the
   * builder with a starting criterion.
   */

  let query = $state('');
  let open = $state(false);
  let looking = $state(false);
  let opening = $state('');
  let segments = $state<SegmentSuggestion[]>([]);
  let companies = $state<CompanySuggestion[]>([]);
  let refusal = $state('');
  let container = $state<HTMLElement | null>(null);
  let input = $state<HTMLInputElement | null>(null);

  const tooShort = $derived(query.trim().length < 3);
  const hasAnswers = $derived(segments.length > 0 || companies.length > 0);

  let timer: ReturnType<typeof setTimeout> | undefined;

  /**
   * Ask a moment after the typing stops.
   *
   * Debounced rather than per keystroke: "elektroinstallation" is nineteen
   * requests otherwise, eighteen of them answers nobody will read.
   */
  function typed() {
    clearTimeout(timer);
    refusal = '';
    open = true;

    if (tooShort) {
      segments = [];
      companies = [];
      return;
    }
    timer = setTimeout(look, 200);
  }

  async function look() {
    const wanted = query.trim();
    looking = true;
    try {
      const found = await suggest(wanted);
      // The answer to a query no longer in the box is not an answer. Two
      // requests can be in flight when somebody types quickly, and without
      // this the slower one wins.
      if (wanted !== query.trim()) return;

      segments = found.segments;
      companies = found.companies;
    } catch (error) {
      segments = [];
      companies = [];
      refusal = error instanceof SearchRefused ? error.message : $_('home.search.failed');
    } finally {
      looking = false;
    }
  }

  function dismiss() {
    open = false;
    clearTimeout(timer);
  }

  function clear() {
    query = '';
    segments = [];
    companies = [];
    refusal = '';
    open = false;
    input?.focus();
  }

  /** Straight to the profile. This is the whole point of the control. */
  function openCompany(company: CompanySuggestion) {
    dismiss();
    void goto(`/company/${company.id}`);
  }

  /**
   * Hand off to the builder, seeded with this industry.
   *
   * The same store the industry door writes, so what the customer gets is a
   * proper selection they can filter and buy — not a list of companies that
   * happen to contain a word.
   */
  async function openSegment(segment: SegmentSuggestion) {
    if (opening) return;
    opening = `segment:${segment.id}`;
    try {
      selectedIndustryCentroids.set(new Map([[String(segment.id), segment.name]]));
      showResults();
      dismiss();
      await submitFilters();
    } catch {
      notifications.error($_('home.search.failed'));
    } finally {
      opening = '';
    }
  }

  function onWindowClick(event: MouseEvent) {
    if (!open || !container) return;
    if (!container.contains(event.target as Node)) open = false;
  }

  function onKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && open) {
      event.stopPropagation();
      dismiss();
      input?.blur();
    }
  }
</script>

<svelte:window on:click={onWindowClick} on:keydown={onKeydown} />

<!--
  Width is this component's business; where it sits is the bar's. The mount
  site centres it — see the top bar in +layout.
-->
<div class="relative w-full min-w-0 max-w-xl" bind:this={container}>
  <div class="chrome-control flex items-center gap-2 rounded-control px-3 py-1.5">
    <Search class="h-4 w-4 shrink-0 text-white/55" />
    <input
      bind:this={input}
      type="search"
      bind:value={query}
      oninput={typed}
      onfocus={() => (open = query.trim().length > 0)}
      placeholder={$_('topbarSearch.placeholder')}
      aria-label={$_('topbarSearch.placeholder')}
      class="input-plain min-w-0 flex-1 text-sm text-white placeholder:text-white/55"
    />
    {#if looking}
      <Loader2 class="h-3.5 w-3.5 shrink-0 animate-spin text-white/55" />
    {:else if query}
      <button type="button" onclick={clear} class="chrome-ghost shrink-0 rounded-control p-0.5">
        <span class="sr-only">{$_('buttons.clearAll')}</span>
        <X class="h-3.5 w-3.5" />
      </button>
    {/if}
  </div>

  {#if open && !tooShort}
    <div
      class="absolute left-0 right-0 z-40 mt-1 overflow-hidden rounded-surface border border-hairline bg-white shadow-overlay"
    >
      {#if refusal}
        <p class="px-4 py-3 text-sm text-red-600">{refusal}</p>
      {:else if !hasAnswers && !looking}
        <p class="px-4 py-3 text-sm text-gray-500">
          {$_('topbarSearch.nothing', { values: { query: query.trim() } })}
        </p>
      {:else}
        {#if segments.length > 0}
          <p
            class="border-b border-hairline px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500"
          >
            {$_('home.groups.segments')}
          </p>
          <ul class="border-b border-hairline">
            {#each segments as segment (segment.id)}
              <li>
                <button
                  type="button"
                  onclick={() => openSegment(segment)}
                  disabled={opening !== ''}
                  class="flex w-full items-center gap-3 px-4 py-2 text-left transition-colors hover:bg-surface-canvas disabled:opacity-50"
                >
                  <Layers class="h-4 w-4 shrink-0 text-accent-600" />
                  <span class="min-w-0 flex-1 truncate text-sm text-gray-900">{segment.name}</span>
                  {#if opening === `segment:${segment.id}`}
                    <Loader2 class="h-3.5 w-3.5 shrink-0 animate-spin text-accent-600" />
                  {:else}
                    <span class="shrink-0 text-xs text-gray-500">
                      {$_('home.groups.companyCount', { values: { count: segment.companies } })}
                    </span>
                  {/if}
                </button>
              </li>
            {/each}
          </ul>
        {/if}

        {#if companies.length > 0}
          <p
            class="border-b border-hairline px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500"
          >
            {$_('home.groups.companies')}
          </p>
          <ul>
            {#each companies as company (company.id)}
              <li>
                <button
                  type="button"
                  onclick={() => openCompany(company)}
                  class="flex w-full items-center gap-3 px-4 py-2 text-left transition-colors hover:bg-surface-canvas"
                >
                  <Building2 class="h-4 w-4 shrink-0 text-gray-400" />
                  <!--
                    The name, and the web address to tell two of them apart.

                    This row used to end in the scopes the match came from —
                    "Name, Website" — which said why our search found it rather
                    than which company it is. Company names repeat here; the
                    domain is the shortest thing that answers "which one", and
                    it is often what the customer typed.
                  -->
                  <span class="min-w-0 flex-1 truncate text-sm text-gray-900">{company.name}</span>
                  {#if company.domain}
                    <span class="hidden shrink-0 text-xs text-gray-400 sm:inline">
                      {company.domain}
                    </span>
                  {/if}
                </button>
              </li>
            {/each}
          </ul>
        {/if}
      {/if}
    </div>
  {/if}
</div>
